import { Card, Col, Form, Row, Select } from "antd";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FOLDING_REQUIREMENT_ADD,
  FOLDING_REQUIREMENT_STYLE_LISTS,
  FOLDING_REQUIREMENT_UPDATE,
  UNIT_PRICE_PO_UPDATE,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import { alertPop } from "../../../apiServices/common/helper";
import { isArrayAndHasValue } from "../../../utils/functions";
import DetailsTable from "./DetailsTable";
import HandleNotification from "common/Notification";

const AddFoldingRequirementForm = forwardRef((props, ref) => {
  const { info, view, poDetailsData, setPoDetailsData, mode } = props;
  const [styles, setStyles] = useState([]);
  const [stylesLoaded, setStylesLoaded] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [polyData, setPolyData] = useState([]);
  const [duplicatePolyError, setDuplicatePolyError] = useState(false);

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { Option } = Select;

  const getStylesList = useCallback(
    async (filterValues) => {
      const bodyData = {
        per_page: filterValues?.per_page || 20,
        ...filterValues,
      };
      const response = await getData(FOLDING_REQUIREMENT_STYLE_LISTS, false, bodyData);

      if (response && response?.data?.code === 200) {
        let stylesResponse = response.data.data?.map((item) => ({
          ...item.style,
        }));

        if (info?.style_info && !stylesResponse.some((s) => s.id === info.style_info.id)) {
          stylesResponse = [
            {
              id: info.style_info.id,
              style_no: info.style_info.style_no,
              sales_contract: info.sales_contract_info,
              buyers: info.buyer_info,
              sub_contract_factory: info.sub_contract_factory_info,
              sub_styles: info.sub_style_info ? [info.sub_style_info] : [],
              po_maps: info.po_map_info ? [info.po_map_info] : [],
              colors: info.color_info ? [info.color_info] : [],
              buying_office: info.buying_office_info,

              sizes: Array.from(
                new Set(
                  info.folding_requirement_polys?.[0]?.poly_size_ratios?.map((ratio) => ({
                    id: ratio.size_info.id,
                    name: ratio.size_info.name,
                  })) || [],
                ),
              ),
            },
            ...stylesResponse,
          ];
        }

        setStyles(stylesResponse || []);
        setStylesLoaded(true);
      }
    },
    [info, view, mode, form],
  );

  useEffect(() => {
    if (info && stylesLoaded && styles.length > 0 && (view || mode === "edit")) {
      handleStyleSelect(info.style_id);

      if (mode === "edit" && info.folding_requirement_polys) {
        const sizeMap = new Map();
        info.folding_requirement_polys.forEach((poly) => {
          poly.poly_size_ratios.forEach((ratio) => {
            if (!sizeMap.has(ratio.size_info.id)) {
              sizeMap.set(ratio.size_info.id, {
                id: ratio.size_info.id,
                name: ratio.size_info.name,
              });
            }
          });
        });
        const existingSizes = Array.from(sizeMap.values());
        setSelectedSizes(existingSizes);

        form.setFieldValue(
          "size_ids",
          existingSizes.map((size) => size.id),
        );
      }

      const formValues = {
        style_id: info.style_id,
        sales_contract_id: info.sales_contract_id,
        buyer_id: info.buyer_id,
        sub_contract_factory_id: info.sub_contract_factory_id,
        sub_style_id: info.sub_style_id,
        po_map_id: info.po_map_id,
        color_id: info.color_id,
        size_ids: info.folding_requirement_polys?.[0]?.poly_size_ratios?.map((ratio) => ratio.size_info.id),
      };

      form.setFieldsValue(formValues);

      if (info.folding_requirement_polys) {
        setPolyData(
          info.folding_requirement_polys.map((poly) => ({
            id: poly.id,
            ratio_name: poly.ratio_name,
            size_ratios: poly.poly_size_ratios.map((ratio) => ({
              id: ratio.id,
              size_id: ratio.size_info.id,
              quantity: ratio.quantity,
            })),
          })),
        );
      }
    }
  }, [info, stylesLoaded, styles, view, mode, form]);

  useImperativeHandle(ref, () => ({
    submit() {
      form
        .validateFields()
        .then(async (values) => {
          if (duplicatePolyError) {
            alertPop("error", "Please resolve duplicate Poly No. errors before submitting.");
            return;
          }
          const payload = {
            style_id: values.style_id,
            sales_contract_id: values.sales_contract_id,
            buying_office_id: selectedStyle?.buying_office?.id || null,
            buyer_id: values.buyer_id || null,
            sub_contract_factory_id: values.sub_contract_factory_id || null,
            sub_style_id: values.sub_style_id || null,
            po_map_id: values.po_map_id || null,
            color_id: values.color_id,
            details: polyData.map((detail) => ({
              id: detail.id || 0,
              ratio_name: detail.ratio_name,
              status: "Created",
              is_delete: detail.is_delete || 0,
              size_ratios: (detail.size_ratios || []).map((sr) => ({
                id: sr.id || 0,
                size_id: sr.size_id,
                quantity: sr.quantity,
                is_delete: sr.is_delete || 0,
              })),
            })),
          };

          let query = info?.id ? `${FOLDING_REQUIREMENT_UPDATE}/${info?.id}` : FOLDING_REQUIREMENT_ADD;
          const response = await postData(query, payload);
          if (response && response?.code === 200) {
            HandleNotification(
              "success",
              "bottomRight",
              `Units Price ${info?.id ? "updated" : "created"} successfully`,
              null,
            );
            navigate("/digitalization/folding-requirement");
          } else {
            HandleNotification("error", "bottomRight", `Error ${info?.id ? "updating" : "creating"} Units Price`, null);
          }
        })
        .catch((errorInfo) => {
          alertPop("error");
        });
    },
    discart() {
      form.resetFields();
    },
  }));

  const handleStyleSelect = (styleId) => {
    const style = styles.find((s) => s.id === styleId);
    if (style) {
      setSelectedStyle(style);

      setDuplicatePolyError(false);

      const fieldsToReset = [
        "sales_contract_id",
        "buyer_id",
        "sub_contract_factory_id",
        "sub_style_id",
        "po_map_id",
        "color_id",
        "size_ids",
      ];
      form.resetFields(fieldsToReset);

      setPolyData([]);

      const formValues = {
        sales_contract_id: style.sales_contract?.id,
      };

      if (style.buyers) {
        formValues.buyer_id = style.buyers.id;
      }

      if (style.sub_contract_factory) {
        formValues.sub_contract_factory_id = style.sub_contract_factory.id;
      }

      if (isArrayAndHasValue(style.sub_styles)) {
        formValues.sub_style_id = undefined;
      }

      if (isArrayAndHasValue(style.po_maps)) {
        formValues.po_map_id = undefined;
      }

      if (mode !== "edit") {
        if (isArrayAndHasValue(style.sizes)) {
          formValues.size_ids = style.sizes.map((size) => size.id);
          setSelectedSizes(style.sizes.map((size) => ({ id: size.id, name: size.name })));
        } else {
          setSelectedSizes([]);
        }
      }

      form.setFieldsValue(formValues);
    }
  };

  const handleStyleClear = () => {
    setSelectedStyle(null);
    setSelectedSizes([]);
    setPolyData([]);

    setDuplicatePolyError(false);
    form.resetFields();
  };

  const handleSizeChange = (sizeIds) => {
    if (isArrayAndHasValue(selectedStyle?.sizes)) {
      const sizes = selectedStyle.sizes
        .filter((size) => sizeIds.includes(size.id))
        .map((size) => ({ id: size.id, name: size.name }));
      setSelectedSizes(sizes);

      if (polyData && polyData.length > 0) {
        const updatedPolyData = polyData.map((poly) => ({
          ...poly,
          size_ratios: [
            ...poly.size_ratios.filter((ratio) => sizeIds.includes(ratio.size_id)),
            ...sizeIds
              .filter((id) => !poly.size_ratios.some((ratio) => ratio.size_id === id))
              .map((id) => ({
                id: 0,
                size_id: id,
                quantity: 0,
              })),
          ],
        }));

        setPolyData(updatedPolyData);
      }

      setDuplicatePolyError(false);
    }
  };

  useEffect(() => {
    getStylesList();
  }, [getStylesList]);

  const filterOption = (input, option) => {
    if (!input || !option) return false;

    const optionValue = (option.children || "").toString().trim();
    const searchValue = input.toString().trim();

    if (optionValue === searchValue) return true;

    return optionValue.toLowerCase().includes(searchValue.toLowerCase());
  };

  return (
    <div className="containt-body">
      <Form disabled={view || mode === "view"} layout="vertical" form={form} name="control-hooks">
        <Card>
          <Row gutter={6}>
            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Style"
                name="style_id"
                rules={[
                  {
                    required: true,
                    message: "Please Select Style!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select a Style"
                  onSelect={handleStyleSelect}
                  optionFilterProp="children"
                  size="small"
                  onSearch={(value) => {
                    getStylesList({ style_no: value });
                  }}
                  allowClear
                  onClear={handleStyleClear}
                  disabled={view || mode === "view" || mode === "edit"}
                >
                  {isArrayAndHasValue(styles) &&
                    styles.map((item, index) => (
                      <Option value={item.id} key={index}>
                        {item.style_no}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Sales Contract"
                name="sales_contract_id"
                rules={[
                  {
                    required: true,
                    message: "Please select Sales Contract!",
                  },
                ]}
              >
                <Select placeholder="Select Sales Contract" size="small" disabled>
                  {selectedStyle?.sales_contract && (
                    <Option value={selectedStyle.sales_contract.id}>{selectedStyle.sales_contract.reference_no}</Option>
                  )}
                </Select>
              </Form.Item>
            </Col>

            {selectedStyle?.buyers ? (
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Buyer"
                  name="buyer_id"
                  rules={[
                    {
                      required: true,
                      message: "Please select Buyer!",
                    },
                  ]}
                >
                  <Select placeholder="Select Buyer" size="small" disabled>
                    <Option value={selectedStyle.buyers.id}>{selectedStyle.buyers.name}</Option>
                  </Select>
                </Form.Item>
              </Col>
            ) : (
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Sub Contract Factory"
                  name="sub_contract_factory_id"
                  rules={[
                    {
                      required: true,
                      message: "Please select Sub Contract Factory!",
                    },
                  ]}
                >
                  <Select placeholder="Select Sub Contract Factory" size="small" disabled>
                    {selectedStyle?.sub_contract_factory && (
                      <Option value={selectedStyle.sub_contract_factory.id}>
                        {selectedStyle.sub_contract_factory.name}
                      </Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>
            )}

            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Sub Style"
                name="sub_style_id"
                rules={[
                  {
                    required: isArrayAndHasValue(selectedStyle?.sub_styles),
                    message: "Please select Sub Style!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select Sub Style"
                  size="small"
                  disabled={view}
                  filterOption={filterOption}
                  optionFilterProp="children"
                >
                  {isArrayAndHasValue(selectedStyle?.sub_styles) &&
                    selectedStyle.sub_styles.map((subStyle, index) => (
                      <Option value={subStyle.id} key={index}>
                        {subStyle.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item
                label="PO"
                name="po_map_id"
                rules={[
                  {
                    required: isArrayAndHasValue(selectedStyle?.po_maps),
                    message: "Please select PO!",
                  },
                ]}
              >
                <Select
                  placeholder="Select PO"
                  size="small"
                  showSearch
                  filterOption={filterOption}
                  optionFilterProp="children"
                  disabled={!isArrayAndHasValue(selectedStyle?.po_maps) || view || mode === "view"}
                >
                  {isArrayAndHasValue(selectedStyle?.po_maps) &&
                    selectedStyle.po_maps.map((po, index) => (
                      <Option value={po.id} key={index}>
                        {po.original_po}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Color"
                filterOption={filterOption}
                optionFilterProp="children"
                name="color_id"
                rules={[
                  {
                    required: true,
                    message: "Please select Color!",
                  },
                ]}
              >
                <Select
                  showSearch
                  filterOption={filterOption}
                  optionFilterProp="children"
                  placeholder="Select Color"
                  size="small"
                >
                  {isArrayAndHasValue(selectedStyle?.colors) &&
                    selectedStyle.colors.map((color, index) => (
                      <Option value={color.id} key={index}>
                        {color.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col
              className="gutter-row"
              span={Math.max(
                4,
                selectedStyle?.sizes && selectedStyle.sizes.length
                  ? Math.min(12, Math.ceil(selectedStyle.sizes.length / 2))
                  : 4,
              )}
            >
              <Form.Item
                label="Sizes"
                name="size_ids"
                rules={[
                  {
                    required: true,
                    message: "Please select Sizes!",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select Sizes"
                  filterOption={filterOption}
                  optionFilterProp="children"
                  size="small"
                  onChange={handleSizeChange}
                  disabled={view || mode === "edit"}
                >
                  {isArrayAndHasValue(selectedStyle?.sizes) &&
                    selectedStyle.sizes.map((size, index) => (
                      <Option value={size.id} key={index}>
                        {size.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>

      {selectedStyle && isArrayAndHasValue(selectedStyle.sizes) && (
        <DetailsTable
          selectedSizes={selectedSizes}
          polyData={polyData}
          setPolyData={setPolyData}
          setDuplicatePolyError={setDuplicatePolyError}
          info={info}
          mode={mode}
        />
      )}
    </div>
  );
});

export default AddFoldingRequirementForm;
